export const LOCATION_STORAGE_KEY = 'domjazz-location-v1';
export const DEFAULT_RADIUS = 30;
export const RADII = [10, 30, 50, 100, 200];

export const hasCoordinates = (point) => Number.isFinite(point?.lat) && Number.isFinite(point?.lon)
  && Math.abs(point.lat) <= 90 && Math.abs(point.lon) <= 180;

export function distanceKm(from, to) {
  if (!hasCoordinates(from) || !hasCoordinates(to)) return null;
  const radians = (degrees) => (degrees * Math.PI) / 180;
  const dLat = radians(to.lat - from.lat);
  const dLon = radians(to.lon - from.lon);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(radians(from.lat)) * Math.cos(radians(to.lat)) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(Math.min(1, a)));
}

// The CMS has venue coordinates, but no city centres. Use their mean as an
// approximate origin for manual city choices; never invent missing coordinates.
export function cityArea(city) {
  const points = (city.lieu || []).map((place) => place.geoAddress).filter(hasCoordinates);
  return {
    kind: 'city',
    slug: city.slug,
    name: city.name,
    lat: points.length ? points.reduce((sum, point) => sum + point.lat, 0) / points.length : null,
    lon: points.length ? points.reduce((sum, point) => sum + point.lon, 0) / points.length : null,
  };
}

export function parseLocationPreferences(serialized, cities) {
  const defaults = { area: null, radius: DEFAULT_RADIUS };
  try {
    const stored = JSON.parse(serialized);
    if (stored?.version !== 1 || !RADII.includes(stored.radius)) return defaults;
    let area = null;
    if (stored.area?.kind === 'city') {
      const city = cities.find((candidate) => candidate.slug === stored.area.slug);
      if (city) area = cityArea(city);
    } else if (stored.area?.kind === 'position' && hasCoordinates(stored.area)) {
      area = { kind: 'position', lat: stored.area.lat, lon: stored.area.lon };
    }
    return { area, radius: stored.radius };
  } catch {
    return defaults;
  }
}

export function areaLabel(area, radius) {
  if (!area) return 'En Provence-Alpes-Côte d’Azur';
  if (!hasCoordinates(area)) return `À ${area.name}`;
  return `${area.kind === 'city' ? `Autour de ${area.name}` : 'Autour de vous'} · ${radius} km`;
}

export function parisClock(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now);
  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return { date: `${values.year}-${values.month}-${values.day}`, minutes: Number(values.hour) * 60 + Number(values.minute) };
}

function addDays(date, days) {
  const value = new Date(`${date}T12:00:00Z`);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}

export function dateWindow(period, today) {
  if (period === 'weekend') {
    const weekday = new Date(`${today}T12:00:00Z`).getUTCDay();
    const start = weekday === 0 ? today : addDays(today, Math.max(0, 6 - weekday));
    return { startDate: start, endDate: weekday === 0 ? today : addDays(start, 1) };
  }
  if (period === 'all') return { startDate: today, endDate: null };
  const days = { today: 1, fortnight: 14, month: 30 }[period] || 14;
  return { startDate: today, endDate: addDays(today, days - 1) };
}

export function timeMinutes(value) {
  if (!value || typeof value !== 'string') return null;
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(value)) {
    const [hours, minutes] = value.split(':').map(Number);
    return hours < 24 && minutes < 60 ? hours * 60 + minutes : null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : parisClock(date).minutes;
}

export function eventTimes(event) {
  return [...new Set((event.entry_hours?.entryHours || [])
    .map(({ startTime }) => timeMinutes(startTime)).filter((time) => time !== null))].sort((a, b) => a - b);
}

export const formatTime = (minutes) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

export function isEventInArea(event, area, radius = DEFAULT_RADIUS) {
  if (!area) return true;
  const distance = distanceKm(area, event.place?.geoAddress);
  if (distance !== null) return distance <= radius;
  return area.kind === 'city' && area.slug === event.place?.city?.slug;
}

export function selectNearbyEvents(events, {
  area = null, radius = DEFAULT_RADIUS, startDate = null, endDate = null,
  today = null, nowMinutes = 0, includeUnavailable = false,
} = {}) {
  return events.flatMap((event) => {
    const date = event.event_date?.slice(0, 10);
    if (!date || (startDate && date < startDate) || (endDate && date > endDate)) return [];
    if (!includeUnavailable && ['Annulé', 'Reporté'].includes(event.status)) return [];
    // Keep today's events without a known time. Known sessions must still be
    // upcoming or in progress (including sessions that end after midnight).
    const sessions = event.entry_hours?.entryHours || [];
    if (date === today && sessions.length && sessions.every(({ startTime, endTime }) => {
      const start = timeMinutes(startTime);
      const end = timeMinutes(endTime);
      if (start === null || (end !== null && end < start)) return false;
      return (end ?? start) < nowMinutes;
    })) return [];
    if (!isEventInArea(event, area, radius)) return [];
    const distance = area ? distanceKm(area, event.place?.geoAddress) : null;
    return [{ ...event, distance }];
  }).sort((a, b) => a.event_date.slice(0, 10).localeCompare(b.event_date.slice(0, 10))
    || (a.distance ?? Infinity) - (b.distance ?? Infinity)
    || (eventTimes(a)[0] ?? Infinity) - (eventTimes(b)[0] ?? Infinity)
    || a.title.localeCompare(b.title, 'fr'));
}
